export type InputPalette = Record<string, string | undefined>;

export type ChannelPalette<T extends InputPalette> = T & {
  [K in keyof T as `${string & K}Channel`]: string;
};

/**
 * Converts a hex color to RGB channels.
 *
 * @param {string} hexColor - The hex color string.
 * @returns {string} - The RGB channels string.
 * @throws {Error} - Throws an error if the hex color is invalid.
 *
 * @example
 * const rgbChannel = hexToRgbChannel("#C8FAD6");
 * console.log(rgbChannel); // "200 250 214"
 */
export function hexToRgbChannel(hexColor: string): string {
  let hex = hexColor.replace(/^#/, '');
  if (![3, 6].includes(hex.length)) {
    throw new Error(`Invalid hex color: ${hexColor}`);
  }
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((c) => c + c)
      .join('');
  }
  const intVals = [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16));
  if (intVals.some((v) => isNaN(v))) {
    throw new Error(`Invalid hex color: ${hexColor}`);
  }
  return intVals.join(' ');
}

/**
 * Converts a hex palette color to RGB channels palette.
 *
 * @param {InputPalette} hexPalette - The input palette object.
 * @returns {ChannelPalette} - The output palette object with RGB channels.
 *
 * @example
 * const palette = createPaletteChannel({
 *   lighter: "#C8FAD6",
 *   light: "#5BE49B",
 *   main: "#00A76F",
 * });
 * console.log(palette);
 * // {
 * //   lighter: "#C8FAD6",
 * //   light: "#5BE49B",
 * //   main: "#00A76F",
 * //   lighterChannel: "200 250 214",
 * //   lightChannel: "91 228 155",
 * //   mainChannel: "0 167 111",
 * // }
 */
export function createPaletteChannel<T extends InputPalette>(hexPalette: T): ChannelPalette<T> {
  const result = { ...hexPalette } as ChannelPalette<T>;
  for (const key in hexPalette) {
    const hex = hexPalette[key];
    if (hex) {
      (result as any)[`${key}Channel`] = hexToRgbChannel(hex);
    }
  }
  return result;
}

/**
 * Adds an alpha channel to a color.
 *
 * @param {string} color - The color string in RGB channels or CSS variable format.
 * @param {number} [opacity=1] - The opacity value.
 * @returns {string} - The color string with alpha channel.
 * @throws {Error} - Throws an error if the color format is unsupported.
 *
 * @example
 * const rgbaColor = varAlpha('200 250 214', 0.8);
 * console.log(rgbaColor); // "rgba(200 250 214 / 0.8)"
 *
 * const rgbaVarColor = varAlpha('var(--palette-primary-lighterChannel)', 0.8);
 * console.log(rgbaVarColor); // "rgba(var(--palette-primary-lighterChannel) / 0.8)"
 */
export function varAlpha(color: string, opacity: number = 1): string {
  if (typeof color !== 'string') {
    throw new Error('Color must be a string');
  }

  const normalized = color.trim();

  // Already in channel format or CSS variable.
  if (/^\d+\s+\d+\s+\d+$/.test(normalized) || normalized.startsWith('var(')) {
    return `rgba(${normalized} / ${opacity})`;
  }

  // Hex colors (e.g. #fff or #ffffff) get converted to channels.
  if (/^#(?:[0-9a-fA-F]{3}){1,2}$/.test(normalized)) {
    return `rgba(${hexToRgbChannel(normalized)} / ${opacity})`;
  }

  throw new Error('Unsupported color format for varAlpha');
}
